// frontend/src/components/common/RichCommentEditor.js
// CKEditor wrapper shared by Part/Ticket comments (create + thread reply).
// Pasting a table (Excel/Word/email) or an image renders it as a real
// HTML table / <img>, not the old monospace-aligned plain text. Pasted
// images are uploaded to the server via SimpleUploadAdapter and referenced
// by URL -- the resulting HTML is sanitized again server-side before it's
// ever stored, so this editor's own permissiveness is not the security
// boundary.
import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
    ClassicEditor, Essentials, Paragraph, Autoformat,
    Bold, Italic, Underline, Strikethrough, RemoveFormat,
    Link, List, BlockQuote, Undo,
    Table, TableToolbar, TableColumnResize, TableProperties, TableCellProperties,
    Image, ImageUpload, ImageToolbar, ImageStyle, ImageResize, ImageCaption,
    ImageTextAlternative, PasteFromOffice, SimpleUploadAdapter, GeneralHtmlSupport,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import api from '../../services/api';

const EDITOR_CONFIG = {
    licenseKey: 'GPL',
    plugins: [
        Essentials, Paragraph, Autoformat,
        Bold, Italic, Underline, Strikethrough, RemoveFormat,
        Link, List, BlockQuote, Undo,
        Table, TableToolbar, TableColumnResize, TableProperties, TableCellProperties,
        Image, ImageUpload, ImageToolbar, ImageStyle, ImageResize, ImageCaption,
        ImageTextAlternative, PasteFromOffice, SimpleUploadAdapter, GeneralHtmlSupport,
    ],
    toolbar: [
        'undo', 'redo', '|',
        'bold', 'italic', 'underline', 'strikethrough', 'removeFormat', '|',
        'bulletedList', 'numberedList', '|',
        'link', 'blockQuote', 'insertTable', 'uploadImage',
    ],
    table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
    },
    image: {
        toolbar: ['imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|', 'toggleImageCaption', 'imageTextAlternative'],
    },
    // Pasted Office/email markup carries inline styles (colors, borders) that
    // convey real structure -- keep them in the editing model. Final storage
    // is still cut down to an allowlist server-side (see sanitizeHtml.js).
    htmlSupport: {
        allow: [{ name: /^(span|div|p|table|tr|td|th)$/, attributes: true, classes: true, styles: true }],
    },
    simpleUpload: {
        uploadUrl: `${api.defaults.baseURL}/uploads/image`,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
    },
};

/** value/onChange are plain HTML strings -- no editor internals leak out. */
const RichCommentEditor = ({ value, onChange, placeholder, disabled }) => {
    const config = useMemo(() => ({ ...EDITOR_CONFIG, placeholder }), [placeholder]);

    return (
        <Box className="rich-comment-editor">
            <CKEditor
                editor={ClassicEditor}
                config={config}
                data={value || ''}
                disabled={disabled}
                onChange={(event, editor) => onChange(editor.getData())}
            />
        </Box>
    );
};

export default RichCommentEditor;
